import { type JSX, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { getFeedbackPrompt, optOutOfFeedbackPrompt, snoozeFeedbackPrompt } from '../api/feedback.ts';
import { useTokenStore } from '../api/use-api.ts';
import { useMe } from '../lib/use-me.ts';
import { useRoleIntro } from '../lib/use-role-intro.ts';
import { useOptimisticDismissal } from '../lib/use-optimistic-dismissal.ts';
import { cn } from '../lib/utils.ts';
import { ExperienceFeedbackForm } from './ExperienceFeedbackForm.tsx';
import { SaveError } from './SaveError.tsx';
import { Card, buttonClass } from './ui/index.ts';

/**
 * The monthly "how are we doing?" pop-up. The server decides when it is due; closing it any way
 * other than "Don't ask me again" counts as "Not now". It never shows in a session that showed
 * the role walkthrough, so the two never stack.
 */
export function ExperienceFeedbackPrompt(): JSX.Element | null {
  const store = useTokenStore();
  const me = useMe();
  const intro = useRoleIntro();
  const [yieldedToIntro, setYieldedToIntro] = useState(false);
  const [phase, setPhase] = useState<'asking' | 'thanked' | 'closed'>('asking');

  const introShowing = intro !== null;
  useEffect(() => {
    if (introShowing) setYieldedToIntro(true);
  }, [introShowing]);

  const prompt = useQuery({
    queryKey: ['feedback-prompt'],
    queryFn: () => getFeedbackPrompt(store),
    staleTime: Infinity,
    enabled: me.isSuccess,
  });

  const dismissal = useOptimisticDismissal((how: 'snooze' | 'opt-out') =>
    how === 'snooze' ? snoozeFeedbackPrompt(store) : optOutOfFeedbackPrompt(store));

  if (!me.isSuccess || introShowing || yieldedToIntro) return null;
  if (prompt.data?.due !== true || phase === 'closed' || dismissal.hidden('snooze') || dismissal.hidden('opt-out')) return null;

  function close(how: 'snooze' | 'opt-out' | 'done'): void {
    if (how === 'done') setPhase('closed');
    else dismissal.dismiss(how);
  }

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) close(phase === 'thanked' ? 'done' : 'snooze'); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/50 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2',
            'focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
        >
          <Card data-testid="experience-prompt" className="max-h-[88vh] overflow-y-auto p-5 sm:p-6">
            {phase === 'thanked' ? (
              <div className="space-y-4" data-testid="experience-prompt-thanks">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <Dialog.Title className="text-base font-semibold">Thank you for your feedback</Dialog.Title>
                </div>
                <Dialog.Description className="text-sm text-muted-foreground">
                  It helps us decide what to improve next. You can send more any time from Help.
                </Dialog.Description>
                <button type="button" data-testid="experience-prompt-close" onClick={() => close('done')} className={buttonClass('primary')}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <Dialog.Title className="text-base font-semibold">How is HapMetrix working for you?</Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                  Three quick questions. Only the rating is required.
                </Dialog.Description>
                <div className="mt-4">
                  <ExperienceFeedbackForm
                    idPrefix="experience-prompt"
                    onSubmitted={() => setPhase('thanked')}
                    secondaryActions={(
                      <>
                        <button type="button" data-testid="experience-prompt-snooze" onClick={() => close('snooze')} className={buttonClass('secondary')}>
                          Not now
                        </button>
                        <button
                          type="button"
                          data-testid="experience-prompt-opt-out"
                          onClick={() => close('opt-out')}
                          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
                        >
                          Don&apos;t ask me again
                        </button>
                        <SaveError mutation={dismissal.failureOf('snooze')} fallback="Could not save that. Please try again." />
                        <SaveError mutation={dismissal.failureOf('opt-out')} fallback="Could not save that. Please try again." />
                      </>
                    )}
                  />
                </div>
              </>
            )}
          </Card>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
